import React, { useState } from 'react';
import { css } from '../styled-system/css/index';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import SEO, { SITE_URL } from '../components/SEO';
import useCatalogProducts from '../hooks/useCatalogProducts';

const pageStyle = css({
  maxW: '1000px',
  m: '40px auto',
  p: '0 20px',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  selectors: { '@media (max-width: 900px)': { gridTemplateColumns: '1fr' } },
});

const imgBoxStyle = css({
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  bg: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: '20px',
});

const imgStyle = css({
  w: '100%',
  h: '360px',
  objectFit: 'contain',
});

const titleStyle = css({
  color: '#111827',
  mb: '8px',
});

const priceStyle = css({
  color: '#197bbd',
  fontSize: '1.25rem',
  fontWeight: 700,
  m: '8px 0',
});

const paraStyle = css({
  color: '#111827',
  lineHeight: 1.6,
});

const specListStyle = css({
  mt: '10px',
  color: '#6b7280',
});

const actionsStyle = css({
  mt: '16px',
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const qtyStyle = css({
  w: '64px',
  p: '8px',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  bg: '#fff',
  color: '#111827',
});

const btnStyle = css({
  display: 'inline-block',
  p: '12px 18px',
  borderRadius: '6px',
  border: 'none',
  bg: '#197bbd',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'none',
});

const ProductDetail = () => {
  const { slug } = useParams();
  const products = useCatalogProducts();
  const product = products.find((item) => item.slug === slug);
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const hasPrice = typeof product?.price === 'number';

  if (!product) {
    return (
      <div className={pageStyle}>
        <div style={{ gridColumn: '1 / -1', color: '#fff' }}>
          Product not found. <Link to="/products">Back to Products</Link>
        </div>
      </div>
    );
  }

  const onAdd = () => {
    const count = Math.max(1, Number(qty) || 1);
    addItem(product, count);
  };

  const productUrl = `${SITE_URL}/products/${product.slug}`;
  const productImage = `${SITE_URL}${product.image}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: productImage,
    brand: {
      '@type': 'Brand',
      name: 'EziBlank',
    },
    category: 'Server rack airflow management',
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'USD',
      price: product.price.toFixed(2),
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Quantity',
        value: product.quantity,
      },
    ],
  };

  return (
    <div className={pageStyle}>
      <SEO
        title={`${product.name} | Server Rack Airflow Product | EZIBLANK USA`}
        description={`${product.description}. Price: $${product.price.toFixed(2)}.`}
        path={`/products/${product.slug}`}
        image={productImage}
        jsonLd={jsonLd}
      />
      <div className={imgBoxStyle}>
        <img className={imgStyle} src={product.image} alt={product.name} />
      </div>
      <div>
        <h1 className={titleStyle}>{product.name}</h1>
        {hasPrice ? <div className={priceStyle}>PRICE: ${product.price.toFixed(2)}</div> : null}
        <p className={paraStyle}>{product.description}</p>
        {product.specs ? (
          <ul className={specListStyle}>
            {Object.entries(product.specs).map(([k, v]) => (
              <li key={k}>
                <strong style={{ color: '#e1d6c0' }}>{k}:</strong> {String(v)}
              </li>
            ))}
          </ul>
        ) : null}
        <div className={actionsStyle}>
          {hasPrice ? (
            <>
              <input aria-label="Number to order" className={qtyStyle} type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
              <button className={btnStyle} onClick={onAdd}>Add to Cart</button>
              <Link style={{ color: '#197bbd' }} to="/cart">Go to Cart</Link>
            </>
          ) : (
            <Link style={{ color: '#197bbd', fontWeight: 700 }} to="/contact">Contact for pricing</Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
